import { fetchJSON as fetchAPI } from './client';

export type KPIDefinition = {
  id: string;
  kpi_id: string;
  business_name: string;
  technical_name: string;
  description: string;
  owner: string;
  domain: string;
  units: string;
  target_value: number;
  current_value: number;
  variance_pct?: number;
  trend_direction: string;
  refresh_frequency: string;
  visualization_type: string;
};

export type Scorecard = {
  id: string;
  name: string;
  scorecard_type: string;
  overall_score: number;
  kpi_count: number;
  green_count: number;
  amber_count: number;
  red_count: number;
};

export type TrendSeries = {
  id: string;
  kpi_id: string;
  kpi_name: string;
  frequency: string;
  points: { period: string; value: number; target?: number; variance?: number }[];
  moving_avg?: number;
};

export type BenchmarkResult = {
  id: string;
  entity_name: string;
  value: number;
  rank: number;
  percentile: number;
  enterprise_avg: number;
  gap_pct: number;
};

export type DecisionAnalytics = {
  id: string;
  title: string;
  decision_owner: string;
  decision_time: string;
  decision_outcome: string;
  decision_accuracy: number;
  decision_confidence: number;
  decision_cost: number;
  decision_benefit: number;
  module_source: string;
};

export type BusinessInsight = {
  id: string;
  type: string;
  title: string;
  summary: string;
  business_explanation: string;
  confidence: number;
  evidence: string[];
  auto_execute: boolean;
};

export type AnalyticsAlert = {
  id: string;
  alert_type: string;
  title: string;
  description: string;
  severity: string;
  triggered_at: string;
  acknowledged: boolean;
};

export type EnterpriseDashboard = {
  enterprise_readiness: number;
  planning_accuracy: number;
  forecast_accuracy: number;
  inventory_health: number;
  supplier_reliability: number;
  warehouse_readiness: number;
  cash_readiness: number;
  consensus_score: number;
  decision_quality: number;
  recommendation_acceptance: number;
  planning_maturity: number;
  enterprise_confidence_score: number;
  planning_health_index: number;
  decision_quality_index: number;
  enterprise_intelligence_score: number;
  registered_kpis: number;
  active_scorecards: number;
  open_alerts: number;
  pending_insights: number;
};

export type DomainSummary = {
  domain: string;
  kpi_count: number;
  avg_score: number;
  health_status: string;
};

export type PlanningAnalyticsSummary = {
  domains: DomainSummary[];
  last_refreshed: string;
  refresh_time_ms: number;
};

const API_BASE = import.meta.env.VITE_PLANNINGANALYTICS_API ?? '/api/v1';

function paFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const planningAnalyticsApi = {
  enterpriseDashboard: () => paFetch<EnterpriseDashboard>('/enterprise-dashboard'),
  analyticsSummary: () => paFetch<PlanningAnalyticsSummary>('/planning-analytics'),
  listKPIs: (domain?: string) => paFetch<KPIDefinition[]>(`/kpis${domain ? `?domain=${domain}` : ''}`),
  getKPI: (id: string) => paFetch<KPIDefinition>(`/kpis/${id}`),
  listScorecards: (type?: string) => paFetch<Scorecard[]>(`/scorecards${type ? `?type=${type}` : ''}`),
  listTrends: (kpiId?: string) => paFetch<TrendSeries[]>(`/trends${kpiId ? `?kpi_id=${kpiId}` : ''}`),
  listBenchmarks: (kpiId?: string) => paFetch<BenchmarkResult[]>(`/benchmarks${kpiId ? `?kpi_id=${kpiId}` : ''}`),
  listDecisions: () => paFetch<DecisionAnalytics[]>('/decision-intelligence'),
  listInsights: () => paFetch<BusinessInsight[]>('/insights'),
  listAlerts: () => paFetch<AnalyticsAlert[]>('/alerts'),
};
